  
  import { BrandAmbassadorRewardlevels } from "../constants/brandAmbassador";
  // Example progress bar logic: current vs. next threshold
  // Base rate and increment per level, currentRewardLevels 1-4
import {
    SIGNUP_THRESHOLD,
    BASE_RATE,
    BONUS_PER_LEVEL,
    MAX_RATE,
} from "../constants/brandAmbassador"
  /**
   * Calculates the reward level and current bonus rate based on signups.
   *
   * @param {number} signups - The total number of signups.
   * @returns {object} - An object with keys 'currentRewardLevel' and 'currentRate'.
   */
  export function calculateLevelAndRate(signups) {
    if (signups <= 0) {
      return { currentRewardLevel: 1, currentRate: BASE_RATE };
    }
    const currentRewardLevel = Math.floor((signups - 1) / SIGNUP_THRESHOLD) + 1;
    const currentRate = Math.min(BASE_RATE + (currentRewardLevel - 1) * BONUS_PER_LEVEL, MAX_RATE);
    return { currentRewardLevel, currentRate };
  }

  // utils/statusIcons.js
export const getStatusIcon = (status) => {
  switch (status) {
    case "Paid":
      return <span role="img" aria-label="bank">🏦</span>;
    case "Pending":
      return <span role="img" aria-label="money with wings">💸</span>;
    case "Payment Scheduled":
      return <span role="img" aria-label="calendar">📅</span>;
    default:
      return null;
  }
};

/**
 * Computes a breakdown of signup rewards based on the total signups extracted from the description.
 * For simplicity, assume each level (except the last) covers 100 signups.
 * E.g., if totalSignups is 150:
 *  - Member: 100 × $1.00 = $100
 *  - Silver: 50 × $1.25 = $62.50
 *
 * @param {string} description - E.g., "150 Signups"
 * @returns {Array} - Array of objects with { label, amount }.
 */
export const computeBreakdown = (description) => {
  // Extract the number from the description using a regex.
  const match = description.match(/(\d+)/);
  if (!match) return [];
  let totalSignups = Number(match[1]);

  const breakdown = [];
  const levels = BrandAmbassadorRewardlevels;

  // For the first 4 levels, assume a capacity of 100 signups each.
  for (let i = 0; i < levels.length; i++) {
    // For the last level, capacity is unlimited.
    const capacity = i < levels.length - 1 ? 100 : totalSignups;
    if (totalSignups <= 0) break;
    const count = Math.min(totalSignups, capacity);
    breakdown.push({
      label: `${levels[i].name} Signups (${count} × $${levels[i].rate.toFixed(2)})`,
      amount: count * levels[i].rate,
    });
    totalSignups -= count;
  }
  return breakdown;
};


/**
 * Computes hourly earnings based on cumulative signups and hourly signups,
 * accounting for a change in rate when cumulative signups cross a threshold.
 *
 * @param {number} previousCumulative - Cumulative signups before the current hour.
 * @param {number} hourlySignups - Number of signups during the current hour.
 * @param {Array} levels - Array of reward level objects (ordered by increasing points range).
 *                           Each object should have:
 *                           - pointsRange: [min, max]
 *                           - rate: number (the rate for signups in that range)
 * @returns {string} - The computed hourly earnings as a formatted string (e.g. "109.75").
 */
export const computeHourlyEarnings = (previousCumulative, hourlySignups, levels) => {
  let earnings = 0;
  let remainingSignups = hourlySignups;
  let currentCumulative = previousCumulative;

  // Loop over each signup in this hour
  // Instead, loop over levels to determine how many signups fall into each rate.
  for (let i = 0; i < levels.length && remainingSignups > 0; i++) {
    const level = levels[i];
    // Determine the maximum cumulative signups allowed for this level.
    // For levels with an infinite upper bound, use a large number.
    const maxForLevel = level.pointsRange[1] === Infinity ? Infinity : level.pointsRange[1];

    // Calculate how many signups can be accommodated in the current level.
    const availableInLevel = Math.max(0, maxForLevel - currentCumulative);
    const signupsAtThisLevel = Math.min(remainingSignups, availableInLevel);

    earnings += signupsAtThisLevel * level.rate;

    // Update counters.
    currentCumulative += signupsAtThisLevel;
    remainingSignups -= signupsAtThisLevel;
  }

  return earnings.toFixed(2);
};
